// app/frontend/components/inbox/ConversationSidebar.tsx

import { Link } from '@inertiajs/react'
import { Clock, Ghost, Search, Star } from 'lucide-react'
import { SidebarInput } from '@/components/ui/shadcn/sidebar'
import { ConversationFilter } from '@/components/ConversationFilter'
import { FolderSelector } from '@/components/FolderSelector'
import { VisitorAvatar } from '@/components/ui/VisitorAvatar'
import { cn } from '@/lib/utils'
import { formatTimeAgo } from '@/lib/dateUtils'
import { filterConversationsForView } from '@/lib/conversationListUpdate'
import { LoadMoreConversations } from '@/components/inbox/LoadMoreConversations'
import type { ConversationPage } from '@/hooks/usePaginatedConversations'
import type { ConversationListItem } from '@/types/conversation'
import type { FolderOption } from '@/contexts/FilterContext'

interface ConversationSidebarProps {
  conversations: ConversationListItem[]
  currentConversationId: string
  currentFilter: string
  currentFolder: FolderOption
  currentAssignment: string
  currentUserId?: string
  searchQuery: string
  onSearchChange: (query: string) => void
  getConversationPath: (convId: string) => string
  nextCursor: string | null
  onLoadMore: (page: ConversationPage<ConversationListItem>) => void
}

export function ConversationSidebar({
  conversations,
  currentConversationId,
  currentFilter,
  currentFolder,
  currentAssignment,
  currentUserId,
  searchQuery,
  onSearchChange,
  getConversationPath,
  nextCursor,
  onLoadMore
}: ConversationSidebarProps) {
  // Sidebar items are loaded once and mutated locally via cable + STATUS_RELOAD,
  // so the server-side filter no longer self-heals on every navigation. Re-apply
  // it here to drop items that no longer match (e.g. a conversation just
  // resolved while the "Unresolved" filter is selected).
  const visibleConversations = filterConversationsForView(conversations, currentFilter, currentFolder, currentAssignment, currentUserId)
  const filteredConversations = searchQuery
    ? visibleConversations.filter(c =>
        c.visitor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.visitor.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.display_subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.last_message?.content?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : visibleConversations

  return (
    <div className="hidden md:flex w-80 flex-col min-h-0 border-r border-sidebar-border bg-sidebar-secondary text-sidebar-secondary-foreground">
      <div className="gap-3.5 border-b border-sidebar-secondary-accent/30 p-4">
        <div className="flex w-full items-center justify-between mb-3">
          <div className="flex items-center gap-1">
            <FolderSelector />
            <ConversationFilter />
          </div>
          <div className="text-xs text-sidebar-secondary-muted">
            {visibleConversations.filter(c => c.status === 'open').length} open
          </div>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 size-4 text-sidebar-secondary-muted" />
          <SidebarInput
            placeholder="Search conversations..."
            className="pl-8"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {filteredConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center px-4">
            <div className="size-12 rounded-full bg-primary/20 flex items-center justify-center mb-3">
              <Ghost className="size-6 text-primary" />
            </div>
            <p className="text-sm text-sidebar-secondary-muted">
              {searchQuery
                ? "No conversations found"
                : conversations.length > 0
                  ? "No conversations match this filter"
                  : "No conversations yet"}
            </p>
          </div>
        ) : (
          filteredConversations.map((conv) => (
            <Link
              href={`${getConversationPath(conv.id)}?filter=${currentFilter}&folder=${currentFolder}&assignment=${currentAssignment}`}
              key={conv.id}
              only={['conversation', 'messages', 'flash']}
              prefetch="hover"
              // Stale-while-revalidate: instant from cache for a quick
              // hover→click (<3s), then serve-cache-and-revalidate up to 1m so a
              // message that arrived during a long dwell still updates on click.
              cacheFor={['3s', '1m']}
              preserveScroll
              preserveState
              className={cn(
                "flex flex-col items-start gap-2 border-b border-sidebar-secondary-accent/30 p-4 text-sm leading-tight whitespace-nowrap last:border-b-0 cursor-pointer",
                conv.id === currentConversationId
                  ? "bg-sidebar-secondary-accent text-sidebar-secondary-foreground"
                  : "hover:bg-sidebar-secondary-accent/50",
                conv.status === 'closed' && "opacity-60"
              )}
            >
              <div className="flex w-full items-center gap-2">
                <VisitorAvatar
                  name={conv.visitor.name}
                  identified={conv.visitor.identified}
                  countryCode={conv.visitor.country_code}
                />
                <span className="truncate font-medium text-sidebar-secondary-foreground">{conv.visitor.name}</span>
                {conv.last_message_at && (
                  <span className="ml-auto text-xs text-sidebar-secondary-muted flex items-center gap-1">
                    <Clock className="size-3" />
                    {formatTimeAgo(conv.last_message_at)}
                  </span>
                )}
              </div>
              {conv.display_subject && (
                <span className="truncate w-full text-xs font-medium text-sidebar-secondary-foreground">
                  {conv.display_subject}
                </span>
              )}
              {conv.last_message?.content && (
                <span className="line-clamp-2 w-full text-xs whitespace-break-spaces text-sidebar-secondary-muted">
                  {conv.last_message.sender_type === 'User' && (
                    <span className="text-primary">You: </span>
                  )}
                  {conv.last_message.content}
                </span>
              )}
              <div className="flex items-center gap-2">
                {conv.unread && (
                  <div className="size-2 rounded-full bg-blue-500" title="Unread" />
                )}
                {!conv.unread && conv.status === 'open' && (
                  <div className="size-1.5 rounded-full bg-primary" title="Open" />
                )}
                {conv.assignee && (
                  <span className="text-xs text-primary/70 truncate">
                    {conv.assignee.name}
                  </span>
                )}
                {conv.visitor.email && !conv.assignee && (
                  <span className="text-xs text-sidebar-secondary-muted truncate">
                    {conv.visitor.email}
                  </span>
                )}
                {conv.csat_response && (
                  <span
                    className="ml-auto flex items-center gap-0.5 text-xs font-medium text-warning"
                    title={`Visitor rated ${conv.csat_response.rating}/5`}
                  >
                    <Star className="size-3 fill-current" />
                    {conv.csat_response.rating}
                  </span>
                )}
              </div>
            </Link>
          ))
        )}
        {!searchQuery && <LoadMoreConversations cursor={nextCursor} onLoaded={onLoadMore} />}
      </div>
    </div>
  )
}

export default ConversationSidebar
